import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box, InputBase, Paper, List, ListItem, ListItemText,
  ListItemIcon, Chip, Typography, CircularProgress, Divider,
  ClickAwayListener, Popper, Fade,
} from '@mui/material';
import {
  Search, Person, Description, BeachAccess,
  SupportAgent, Article, MenuBook, Work,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { apiFetch } from '../services/apiFetch';

interface SearchResult {
  type: string;
  id: string;
  title: string;
  subtitle?: string;
  meta?: string;
  linkPath: string;
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  'Employee':       <Person fontSize="small" />,
  'Document':       <Description fontSize="small" />,
  'Leave Request':  <BeachAccess fontSize="small" />,
  'Helpdesk Ticket':<SupportAgent fontSize="small" />,
  'Policy':         <Article fontSize="small" />,
  'Knowledge Base': <MenuBook fontSize="small" />,
  'Job Requisition':<Work fontSize="small" />,
};

const TYPE_COLORS: Record<string, string> = {
  'Employee':       '#1976d2',
  'Document':       '#5d4037',
  'Leave Request':  '#388e3c',
  'Helpdesk Ticket':'#00796b',
  'Policy':         '#7b1fa2',
  'Knowledge Base': '#f57c00',
  'Job Requisition':'#d32f2f',
};

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState<SearchResult[]>([]);
  const [loading, setLoading]     = useState(false);
  const [open, setOpen]           = useState(false);
  const [focused, setFocused]     = useState(false);
  const inputRef                  = useRef<HTMLInputElement>(null);
  const anchorRef                 = useRef<HTMLDivElement>(null);
  const debouncedQuery            = useDebounce(query, 280);

  const doSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`/api/search?q=${encodeURIComponent(q)}&limit=25`);
      if (res.ok) {
        const data = await res.json();
        setResults(data.results ?? []);
        setOpen(true);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { doSearch(debouncedQuery); }, [debouncedQuery, doSearch]);

  const handleSelect = (result: SearchResult) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    navigate(result.linkPath);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setQuery(''); setOpen(false); inputRef.current?.blur(); }
  };

  // Group results by type
  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  return (
    <ClickAwayListener onClickAway={() => { setOpen(false); setFocused(false); }}>
      <Box ref={anchorRef} sx={{ position: 'relative', flexGrow: 1, maxWidth: 480, mx: 2 }}>
        {/* Input */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: focused ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.12)',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            border: focused ? '1px solid rgba(255,255,255,0.4)' : '1px solid rgba(255,255,255,0.15)',
            transition: 'all 0.2s',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
          }}
        >
          <Search sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, fontSize: 20 }} />
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => { setFocused(true); if (results.length > 0) setOpen(true); }}
            onKeyDown={handleKeyDown}
            placeholder="Search employees, docs, tickets…"
            sx={{
              color: '#fff',
              flex: 1,
              fontSize: 14,
              '& input::placeholder': { color: 'rgba(255,255,255,0.6)' },
            }}
          />
          {loading && <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.7)' }} />}
        </Box>

        {/* Dropdown */}
        <Popper
          open={open && results.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          transition
          style={{ zIndex: 1400, width: anchorRef.current?.offsetWidth ?? 480 }}
          modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={150}>
              <Paper
                elevation={8}
                sx={{ maxHeight: 520, overflowY: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
              >
                {Object.entries(grouped).map(([type, items], gIdx) => (
                  <Box key={type}>
                    {gIdx > 0 && <Divider />}
                    {/* Category header */}
                    <Box display="flex" alignItems="center" gap={1} px={2} py={0.75} sx={{ bgcolor: 'grey.50' }}>
                      <Box sx={{ color: TYPE_COLORS[type] ?? '#555', display: 'flex', fontSize: 16 }}>
                        {TYPE_ICONS[type] ?? <Article fontSize="small" />}
                      </Box>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {type}
                      </Typography>
                      <Chip label={items.length} size="small" sx={{ height: 18, fontSize: 10 }} />
                    </Box>
                    <List disablePadding>
                      {items.map((r, idx) => (
                        <React.Fragment key={r.id}>
                          {idx > 0 && <Divider component="li" sx={{ ml: 7 }} />}
                          <ListItem
                            onClick={() => handleSelect(r)}
                            sx={{
                              cursor: 'pointer',
                              py: 0.75, px: 2,
                              '&:hover': { bgcolor: `${TYPE_COLORS[r.type] ?? '#1976d2'}08` },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: TYPE_COLORS[r.type] ?? '#555' }}>
                              {TYPE_ICONS[r.type] ?? <Article fontSize="small" />}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight={500} noWrap>
                                  {r.title}
                                </Typography>
                              }
                              secondary={
                                r.subtitle ? (
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {r.subtitle}
                                    {r.meta ? ` · ${r.meta}` : ''}
                                  </Typography>
                                ) : undefined
                              }
                            />
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  </Box>
                ))}
                {results.length === 0 && (
                  <Box p={3} textAlign="center">
                    <Typography variant="body2" color="text.secondary">No results found</Typography>
                  </Box>
                )}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
